import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import DOMPurify from "isomorphic-dompurify";
import { useDeferredValue, useState } from "react";
import { z } from "zod";
import { api } from "@/shared/api/client";
import { ctMimeToBadgeLabel } from "@/features/catalogs/code-file-reader";
import { EmptyState, Field, StateMatrix } from "@/shared/ui";

const SearchHit = z
  .object({
    catalogName: z.string(),
    slug: z.string(),
    revisionId: z.string(),
    title: z.string(),
    description: z.string().optional(),
    category: z.string(),
    score: z.number(),
    headline: z.string(),
    snippets: z.array(z.unknown()).optional(),
    contentType: z.string().optional(),
  })
  .passthrough();
type SearchHit = z.infer<typeof SearchHit>;

const SearchResponse = z.object({
  data: z.array(SearchHit),
});

export function SearchPage() {
  const [q, setQ] = useState("");
  const debounced = useDeferredValue(q);
  const trimmed = debounced.trim();

  const query = useQuery({
    queryKey: ["search", trimmed],
    enabled: trimmed.length >= 2,
    queryFn: async () => {
      const raw = await api<unknown>(`/v1/search?q=${encodeURIComponent(trimmed)}&limit=30`);
      return SearchResponse.parse(raw).data;
    },
  });

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col gap-4 p-8">
      <header>
        <h1 className="text-2xl font-semibold">Search</h1>
        <p className="mt-1 text-sm text-[var(--fg-muted)]">
          Full-text search across every catalog you can read.
        </p>
      </header>
      <Field
        label="Query"
        placeholder="e.g. middleware, authentication, webhook"
        autoFocus
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
      />

      {trimmed.length < 2 ? (
        <p className="text-xs text-[var(--fg-muted)]">Type at least 2 characters.</p>
      ) : (
        <StateMatrix
          status={query.status}
          data={query.data}
          error={query.error}
          onRetry={() => query.refetch()}
          isEmpty={(d) => d.length === 0}
          empty={
            <EmptyState
              title="No results"
              description={`Nothing matched "${trimmed}".`}
            />
          }
        >
          {(hits) => (
            <ul className="flex flex-col gap-3">
              {hits.map((h) => (
                <SearchHitCard key={`${h.catalogName}/${h.slug}/${h.revisionId}`} hit={h} />
              ))}
            </ul>
          )}
        </StateMatrix>
      )}
    </div>
  );
}

function SearchHitCard({ hit }: { hit: SearchHit }) {
  // Backend returns headline with <mark> spans around matched terms. DOMPurify
  // is applied as defense-in-depth even though the source is trusted.
  const safeHtml = DOMPurify.sanitize(hit.headline, {
    ALLOWED_TAGS: ["mark", "strong", "em", "br"],
    ALLOWED_ATTR: [],
  });

  return (
    <li>
      <Link
        to="/catalogs/$name/$"
        params={{ name: hit.catalogName, _splat: hit.slug }}
        className="block rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4 transition-colors hover:bg-[var(--surface-2)]"
      >
        <div className="flex items-baseline justify-between gap-3">
          <p className="truncate text-sm font-semibold">{hit.title}</p>
          <div className="flex shrink-0 items-center gap-1.5">
            {(() => {
              const badge = ctMimeToBadgeLabel(hit.contentType);
              return badge ? (
                <span className="rounded bg-[var(--surface)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--fg-muted)]">
                  {badge}
                </span>
              ) : null;
            })()}
            <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">
              {hit.catalogName}
            </span>
          </div>
        </div>
        <p className="mt-0.5 text-xs text-[var(--fg-muted)]">
          {hit.category} · {hit.slug}
        </p>
        <div
          className="search-headline mt-2 text-sm leading-relaxed text-[var(--fg-muted)]"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized above via DOMPurify.
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      </Link>
    </li>
  );
}
